import Network from 'lib/api/network';

export default {
  namespaced: true,

  state: {
    mySchedules: [],
    availableBirthdayChannels: [],
    selectedBirthdayChannelIds: [],
    editingRestrictedTypes: [],
    managerReminderTypes: [],
    loading: false,
    error: null,
  },

  mutations: {
    setMySchedules(state, schedules) {
      state.mySchedules = schedules;
    },

    setEditingRestrictedTypes(state, editingRestrictedTypes) {
      state.editingRestrictedTypes = editingRestrictedTypes;
    },

    setAvailableBirthdayChannels(state, channels) {
      state.availableBirthdayChannels = channels;
    },

    setSelectedBirthdayChannelIds(state, channelIds) {
      state.selectedBirthdayChannelIds = channelIds;
    },

    setManagerReminderTypes(state, managerReminderTypes) {
      state.managerReminderTypes = managerReminderTypes;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

  },

  actions: {
    fetchMySchedules({ commit, dispatch }) {
      commit('setLoading', true);

      return new Promise((resolve, reject) => {
        const response = Network.get('/celebration_schedules/my_schedules', {
          success: (data) => {
            const { schedules, editingRestrictedTypes, managerReminderTypes } = data;
            const validSchedules = schedules.filter(schedule => schedule !== null);
            commit('setMySchedules', validSchedules);
            commit('setEditingRestrictedTypes', editingRestrictedTypes);
            commit('setManagerReminderTypes', managerReminderTypes);

            // Extract birthday channel data if present
            const birthdaySchedule = validSchedules.find(s => s.celebration_type === 'birthday');
            if (birthdaySchedule && birthdaySchedule.available_channels) {
              commit('setAvailableBirthdayChannels', birthdaySchedule.available_channels);
              commit('setSelectedBirthdayChannelIds', birthdaySchedule.selected_channel_ids || []);
            }
            resolve(data);
          },
          error: (error) => {
            dispatch('setErrorToast', 'Failed to fetch celebration preferences', { root: true });
            reject(error);
          },
          complete: () => {
            commit('setLoading', false);
          },
        });
      });
    },

    upsertMySchedule({ commit, dispatch }, params) {
      commit('setLoading', true);

      const response = Network.patch('/celebration_schedules/upsert_my_schedule', params, {
        success: (data) => {
          dispatch('setSuccessToast', data.message || 'Schedule updated successfully', { root: true });
          // Update the local state with the response data
          dispatch('updateLocalScheduleFromResponse', { params, response: data });

          // Handle birthday channel data if present
          if (params.celebration_type === 'birthday' && data.available_channels) {
            commit('setAvailableBirthdayChannels', data.available_channels);
            commit('setSelectedBirthdayChannelIds', data.selected_channel_ids || []);
          }
        },
        error: (error) => {
          dispatch('setErrorToast', 'Failed to update celebration schedule', { root: true });
          commit('setLoading', false);
        },
        complete: () => {
          commit('setLoading', false);
        },
      });

      return response;
    },

    updateBirthdayChannels({ commit, dispatch }, selectedChannelIds) {
      commit('setLoading', true);

      const response = Network.patch('/celebration_schedules/upsert_my_schedule', {
        celebration_type: 'birthday',
        selected_channel_ids: selectedChannelIds,
      }, {
        success: (data) => {
          // Update the local state with the response data (includes both schedule and channel data)
          if (data.available_channels) {
            commit('setAvailableBirthdayChannels', data.available_channels);
          }
          if (data.selected_channel_ids) {
            commit('setSelectedBirthdayChannelIds', data.selected_channel_ids);
          }
          dispatch('updateLocalScheduleFromResponse', {
            params: { celebration_type: 'birthday' },
            response: data,
          });
        },
        error: (error) => {
          dispatch('setErrorToast', 'Failed to update birthday channel selection', { root: true });
        },
        complete: () => {
          commit('setLoading', false);
        },
      });

      return response;
    },

    updateLocalScheduleFromResponse({ commit, state }, { params, response }) {
      const { celebration_type } = params;
      const updatedSchedules = [...state.mySchedules];

      // Find existing schedule for this celebration type
      const existingIndex = updatedSchedules.findIndex(s => s.celebration_type === celebration_type);

      if (response.schedule === null) {
        // Remove the schedule if response indicates deletion
        if (existingIndex !== -1) {
          updatedSchedules.splice(existingIndex, 1);
        }
      } else if (existingIndex !== -1) {
        // Update existing
        updatedSchedules[existingIndex] = response.schedule;
      } else {
        // Add new
        updatedSchedules.push(response.schedule);
      }

      commit('setMySchedules', updatedSchedules);
    },
  },

  getters: {
    schedules: state => state.mySchedules,
    loading: state => state.loading,
    editingRestrictedTypes: state => state.editingRestrictedTypes,

    birthdaySchedule: state => state.mySchedules.find(s => s.celebration_type === 'birthday'),
    anniversarySchedule: state => state.mySchedules.find(s => s.celebration_type === 'anniversary'),

    birthdayDate: (state, getters) => getters.birthdaySchedule?.date || null,
    anniversaryDate: (state, getters) => getters.anniversarySchedule?.date || null,

    availableBirthdayChannels: state => state.availableBirthdayChannels,
    selectedBirthdayChannelIds: state => state.selectedBirthdayChannelIds,
    managerReminderTypes: state => state.managerReminderTypes,
  },
};
